"use client";

import { formatDate } from "@/lib/utils/formatters";

/**
 * DateDisplay component for consistently formatting dates across the application
 *
 * @param date - The date to display (string, Date, or null/undefined)
 * @param format - Optional custom date format
 * @param fallback - Text to display when date is null/undefined (default: "")
 * @param showTime - Whether to show time in the formatted date (default: false)
 * @param className - Additional CSS class names
 * @param ariaLabel - Optional aria-label to provide additional context about the date
 * @returns Formatted date or fallback text
 */

export enum DateFormats {
  FULL = "MMMM d, yyyy",
  WITH_TIME = "MMMM d, yyyy 'at' h:mm a",
}

function resolveDateFormat(
  format: string | undefined,
  showTime: boolean,
): string {
  return format ?? (showTime ? DateFormats.WITH_TIME : DateFormats.FULL);
}

function toDateObject(date: string | Date): Date {
  return date instanceof Date ? date : new Date(date);
}

export function DateDisplay({
  ariaLabel,
  className = "",
  date,
  fallback = "",
  format,
  showTime = false,
}: {
  ariaLabel: string;
  className?: string;
  date: string | Date | null | undefined;
  fallback?: string;
  format?: string;
  showTime?: boolean;
}) {
  if (!date) {
    return <span className={className}>{fallback}</span>;
  }

  const dateFormat = resolveDateFormat(format, showTime);
  const isoDate = toDateObject(date).toISOString();

  return (
    <time aria-label={ariaLabel} className={className} dateTime={isoDate}>
      {formatDate(date, dateFormat, fallback)}
    </time>
  );
}
